import React, { useState, useEffect, useCallback } from 'react'
import Button from '@atlaskit/button'
import TextField from '@atlaskit/textfield'
import InlineEdit from '@atlaskit/inline-edit'
import Spinner from '@atlaskit/spinner'
import LabelIcon from '@atlaskit/icon/glyph/label'
import UploadIcon from '@atlaskit/icon/glyph/upload'
import TrashIcon from '@atlaskit/icon/glyph/trash'
import AddIcon from '@atlaskit/icon/glyph/add'
import { ConfirmDeleteModal } from '../FolderBrowser/ConfirmDeleteModal'
import { ImportLabelsModal } from './ImportLabelsModal'
import { getActiveProjectId } from '../../constants/config'
import {
  fetchProjectLabels,
  createProjectLabel,
  updateLabel,
  deleteLabel
} from '../../api/testScenarioApi'

/**
 * LABELS MANAGEMENT COMPONENT
 *
 * Full CRUD interface for managing project-level labels in the Settings page.
 * Allows users to view, add, rename, delete, and import labels from Jira.
 *
 * Uses local state with direct API calls (no Redux) since this is a standalone
 * settings section. Scenario pages re-fetch labels on their own mount.
 */
function LabelsManagement() {
  // All project labels fetched from API
  const [labels, setLabels] = useState([])
  // Loading state for initial fetch
  const [isLoading, setIsLoading] = useState(true)
  // Controls visibility of the Import Labels from Jira modal
  const [isImportModalOpen, setIsImportModalOpen] = useState(false)
  // Input value for the add-label text field
  const [newLabelName, setNewLabelName] = useState('')
  // Loading state for add-label operation
  const [isAdding, setIsAdding] = useState(false)
  // Label object targeted for deletion (opens ConfirmDeleteModal when set)
  const [deleteTarget, setDeleteTarget] = useState(null)

  /**
   * LOAD LABELS
   * Fetches all labels for the active project on component mount
   */
  const loadLabels = useCallback(async () => {
    setIsLoading(true)
    try {
      const data = await fetchProjectLabels(getActiveProjectId())
      setLabels(data || [])
    } catch (error) {
      // Silent failure — no notification
    } finally {
      setIsLoading(false)
    }
  }, [])

  // Fetch labels on mount
  useEffect(() => {
    loadLabels()
  }, [loadLabels])

  /**
   * ADD LABEL
   * Creates a new label via API, appends to local state on success
   */
  const handleAddLabel = useCallback(async () => {
    const trimmed = newLabelName.trim()
    if (!trimmed) return

    setIsAdding(true)
    try {
      const created = await createProjectLabel(getActiveProjectId(), trimmed)
      // Check if label already exists in local list (backend returns existing on duplicate)
      const exists = labels.find(l => l.id === created.id)
      if (!exists) {
        setLabels(prev => [...prev, created].sort((a, b) => a.name.localeCompare(b.name)))
      }
      setNewLabelName('')
    } catch (error) {
      // Silent failure — no notification
    } finally {
      setIsAdding(false)
    }
  }, [newLabelName, labels])

  /**
   * RENAME LABEL
   * Updates label name via API, updates local state on success.
   * Called by InlineEdit onConfirm.
   */
  const handleRenameLabel = useCallback(async (labelId, newName) => {
    const trimmed = newName.trim()
    if (!trimmed) return

    // Find current label to check if name actually changed
    const current = labels.find(l => l.id === labelId)
    if (current && current.name === trimmed) return

    try {
      const updated = await updateLabel(labelId, trimmed)
      setLabels(prev =>
        prev
          .map(l => (l.id === labelId ? { ...l, name: updated.name } : l))
          .sort((a, b) => a.name.localeCompare(b.name))
      )
    } catch (error) {
      // Silent failure — no notification
    }
  }, [labels])

  /**
   * DELETE LABEL
   * Permanently removes a label via API, removes from local state on success.
   * Called from ConfirmDeleteModal's onConfirm callback.
   */
  const handleDeleteLabel = useCallback(async (labelId) => {
    await deleteLabel(labelId)
    setLabels(prev => prev.filter(l => l.id !== labelId))
  }, [])

  return (
    <div className="settings-panel-content">

      {/* Header: title + description on the left, Sync button on the right */}
      <div className="labels-management-header">
        <div>
          <h2 className="settings-panel-title">Labels</h2>
          <p className="settings-panel-description">
            Manage project labels. Labels can be assigned to test scenarios for organization and filtering.
          </p>
        </div>
        <Button
          appearance="default"
          iconBefore={<UploadIcon label="Import" size="small" />}
          onClick={() => setIsImportModalOpen(true)}
        >
          Import from Jira
        </Button>
      </div>

      {/* Add label input row: text field + add button */}
      <div className="labels-add-row">
        <div className="labels-add-input">
          <TextField
            value={newLabelName}
            onChange={(e) => setNewLabelName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && newLabelName.trim() && !isAdding) {
                e.preventDefault()
                handleAddLabel()
              }
            }}
            placeholder="Enter label name..."
            isCompact
          />
        </div>
        <Button
          appearance="primary"
          spacing="compact"
          iconBefore={<AddIcon label="Add" size="small" />}
          onClick={handleAddLabel}
          isLoading={isAdding}
          isDisabled={!newLabelName.trim()}
        >
          Add Label
        </Button>
      </div>

      {/* Labels list, loading spinner, or empty state */}
      {isLoading ? (
        /* Loading spinner while fetching labels */
        <div className="labels-loading">
          <Spinner size="medium" />
        </div>
      ) : labels.length === 0 ? (
        /* Empty state when no labels exist */
        <div className="labels-empty-state">
          <LabelIcon label="" size="xlarge" primaryColor="var(--ds-icon-disabled, #A5ADBA)" />
          <p>No labels yet</p>
          <p>Add labels above or import from Jira</p>
        </div>
      ) : (
        /* Labels list - each row has inline-editable name + delete button */
        <div className="labels-list">
          {labels.map(label => (
            <div key={label.id} className="label-row">
              {/* Inline edit for renaming the label */}
              <div className="label-name">
                <InlineEdit
                  key={`${label.id}-${label.name}`}
                  defaultValue={label.name}
                  editView={({ errorMessage, ...fieldProps }) => (
                    <TextField {...fieldProps} autoFocus isCompact />
                  )}
                  readView={() => (
                    <div className="label-name-read">{label.name}</div>
                  )}
                  onConfirm={(value) => handleRenameLabel(label.id, value)}
                />
              </div>

              {/* Delete button - opens confirmation modal */}
              <Button
                appearance="subtle"
                spacing="compact"
                iconBefore={<TrashIcon label="Delete" size="small" />}
                onClick={() => setDeleteTarget(label)}
              />
            </div>
          ))}
        </div>
      )}

      {/* Delete confirmation modal - reuses existing ConfirmDeleteModal */}
      <ConfirmDeleteModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => handleDeleteLabel(deleteTarget.id)}
        title="Delete Label"
        itemName={deleteTarget?.name || ''}
        itemType="label"
        warningMessage="This will permanently remove this label from all test scenarios that use it."
      />

      {/* Import from Jira modal - browse and select Jira labels to import */}
      <ImportLabelsModal
        isOpen={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
        existingLabels={labels}
        onImportComplete={loadLabels}
      />
    </div>
  )
}

export { LabelsManagement }
